import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import {
  IonInput,
  IonItem,
  IonSelect,
  IonSelectOption,
  ToastController,
} from '@ionic/angular';
import { finalize } from 'rxjs';
import { PanelHostService } from '../../../../services/panel-host.service';
import { TripFinanceService } from '../../../../services/trip-finance.service';
import { TripService } from '../../../../services/trip.service';
import {
  BudgetAmountType,
  BUDGET_AMOUNT_MAX,
  CreateBudgetBody,
  NewBudgetWithTypeLabel,
  SELECTABLE_BUDGET_TYPE_OPTIONS,
} from '../../../../models/finance-models';
import { formatDateForInput } from '../../../trip-finances/trip-finance-date.util';
import { ContentContainerComponent } from '../../../ui/content-container/content-container.component';
import { InlineAlertComponent } from '../../../ui/inline-alert/inline-alert.component';
import { PanelBodyComponent } from '../../../ui/panel-body/panel-body.component';

@Component({
  selector: 'app-edit-budget-item-panel',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    ContentContainerComponent,
    InlineAlertComponent,
    PanelBodyComponent,
    IonInput,
    IonItem,
    IonSelect,
    IonSelectOption,
  ],
  templateUrl: './edit-budget-item-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBudgetItemPanelComponent {
  private readonly panelHost = inject(PanelHostService);
  private readonly tripFinanceService = inject(TripFinanceService);
  private readonly tripService = inject(TripService);
  private readonly transloco = inject(TranslocoService);
  private readonly toastController = inject(ToastController);
  private readonly fb = inject(FormBuilder);

  readonly BudgetAmountType = BudgetAmountType;

  readonly budgetTypes = SELECTABLE_BUDGET_TYPE_OPTIONS;

  readonly form = this.fb.group({
    type: this.fb.control<number | null>(null, Validators.required),
    amount: this.fb.control<number | null>(null, [
      Validators.required,
      Validators.min(0.01),
      Validators.max(BUDGET_AMOUNT_MAX),
    ]),
    amountType: this.fb.control<BudgetAmountType | null>(null, Validators.required),
    description: this.fb.control('', [Validators.required, Validators.maxLength(500)]),
    due: this.fb.control('', Validators.required),
  });

  submitting = signal(false);
  isEdit = signal(false);
  recordId = signal<number | null>(null);

  constructor() {
    effect(() => {
      const panel = this.panelHost.activePanel();
      const context = this.panelHost.tripFinancePanelContext();
      if (panel === 'edit-budget-item' && context?.mode === 'budget-item') {
        this.isEdit.set(context.action === 'edit');
        if (context.action === 'edit' && context.record) {
          const record = context.record as NewBudgetWithTypeLabel;
          this.recordId.set(record.id);
          this.form.patchValue({
            type: record.type,
            amount: record.amount,
            amountType: record.amountType,
            description: record.description,
            due: formatDateForInput(record.due),
          });
        } else {
          this.recordId.set(null);
          this.form.reset({
            type: null,
            amount: null,
            amountType: null,
            description: '',
            due: formatDateForInput(new Date()),
          });
        }
      }
    });
  }

  cancel(): void {
    this.panelHost.dismiss();
  }

  save(): void {
    if (this.form.invalid || this.submitting()) {
      this.form.markAllAsTouched();
      return;
    }

    const trip = this.tripService.currentTrip.value();
    if (!trip) {
      return;
    }

    const body: CreateBudgetBody = {
      type: this.form.value.type!,
      amount: Number(this.form.value.amount),
      amountType: this.form.value.amountType!,
      description: this.form.value.description!,
      due: this.form.value.due!,
    };

    this.submitting.set(true);
    const request$ =
      this.isEdit() && this.recordId()
        ? this.tripFinanceService.updateBudgetItem(trip.id, this.recordId()!, body)
        : this.tripFinanceService.createBudgetItem(trip.id, body);

    request$.pipe(finalize(() => this.submitting.set(false))).subscribe({
      next: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(
            marker(this.isEdit() ? 'trip-finances.panels.budget-item.save-success-edit' : 'trip-finances.panels.budget-item.save-success-add')
          ),
          duration: 4000,
          color: 'success',
        });
        await toast.present();
        this.panelHost.dismiss();
      },
      error: async () => {
        const toast = await this.toastController.create({
          message: this.transloco.translate(marker('trip-finances.panels.save-error')),
          duration: 4000,
          color: 'danger',
        });
        await toast.present();
      },
    });
  }
}
